import { SupportError } from './types.js'
import type { SupportEvent, SupportReport } from './types.js'
export const MAX_REPORT_BYTES = 16 * 1024 * 1024
export const bytes = (value: unknown) => new TextEncoder().encode(JSON.stringify(value)).byteLength
export const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value))
const invalid = (message: string): never => { throw new SupportError('InvalidReport', message) }
const finite = (n: unknown) => typeof n === 'number' && Number.isFinite(n)

const safeAsset = (value: unknown): value is string => typeof value === 'string' && value.length <= 3 * 1024 * 1024 && /^data:(image\/(png|jpeg|webp|gif)|font\/(woff2?|otf|ttf));base64,[A-Za-z0-9+/]+={0,2}$/.test(value)
/** Only embedded raster images/fonts and local SVG references survive; replay enforces no network. */
function css(text: string): string {
  return text.replace(/url\s*\(\s*(["']?)([^)]*?)\1\s*\)/gi, (_, quote, url) => safeAsset(url) || /^#[a-zA-Z_][\w:.-]*$/.test(url) ? `url("${url}")` : 'none').replace(/@import[^;]*(;|$)/gi, '')
}
function attributes(attrs: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {}
  for (const [key, value] of Object.entries(attrs || {})) {
    if (/^(rr_dataURL|src|poster)$/.test(key) && safeAsset(value) && value.startsWith('data:image/')) { result[key] = value; continue }
    if (/^(href|xlink:href)$/.test(key) && typeof value === 'string' && /^#[a-zA-Z_][\w:.-]*$/.test(value)) { result[key] = value; continue }
    if (/^data-(closed|enter|leave)$/.test(key) && value === '') { result[key] = ''; continue }
    if (key === 'data-placement' && typeof value === 'string' && /^(top|bottom|left|right)(-start|-end)?$/.test(value)) { result[key] = value; continue }
    // Empty Vue scope markers and bounded presentation state contain no document data.
    if (/^data-v-[a-f0-9]+$/.test(key) && value === '') { result[key] = ''; continue }
    if (/^data-ae-(dialog-mode|dialog-region-kind|popup-open|popup-kind|popup-mobile|overlay-bounded)$/.test(key) && typeof value === 'string' && /^(true|false|popup|dialog|docked|window|windowed|fullscreen|immersive|menu|tooltip|popover|default|application|region|preview|left|right|bottom|center)$/.test(value)) { result[key] = value; continue }
    if (/^(on|data-)/i.test(key) || /^(src|srcset|href|xlink:href|poster|action|formaction|srcdoc|rr_src|rr_dataURL|nonce|integrity)$/i.test(key)) continue
    result[key] = key === 'style' || key === '_cssText' ? (typeof value === 'string' ? css(value) : null) : value
  }
  return result
}
function node(n: any, depth = 0): any {
  if (!n || typeof n !== 'object' || depth > 150) invalid('Invalid snapshot tree')
  if (n.attributes?.rr_width !== undefined && n.attributes?.rr_height !== undefined) {
    return { type: 2, id: n.id, tagName: 'div', attributes: {
      class: 'ae-support-redacted', rr_width: n.attributes.rr_width, rr_height: n.attributes.rr_height,
    }, childNodes: [] }
  }
  if (n.type === 2) {
    n.attributes = attributes(n.attributes)
    if (/^(script|iframe|object|embed|base|meta)$/i.test(n.tagName)) {
      return { type: 2, id: n.id, tagName: 'div', attributes: {}, childNodes: [] }
    }
    if (n.tagName === 'style') for (const child of n.childNodes || []) if (typeof child.textContent === 'string') child.textContent = css(child.textContent)
  }
  if (n.childNodes) n.childNodes = n.childNodes.map((child: any) => node(child, depth + 1))
  return n
}
/** Called on emitted events BEFORE buffering, persistence, or export. */
export function sanitizeEvent(input: SupportEvent): SupportEvent | null {
  const event = clone(input)
  if (event.type === 4) event.data = { width: event.data.width, height: event.data.height, href: 'about:blank' }
  if (event.type === 2) event.data.node = node(event.data.node)
  if (event.type === 3) {
    const d = event.data
    // No canvas, font payloads, log plugins, or network payloads.
    if (![0, 1, 2, 3, 4, 5, 6, 7, 8, 13, 14, 15].includes(d.source)) return null
    if (d.source === 0) {
      d.adds = (d.adds || []).map((a: any) => ({ ...a, node: node(a.node) }))
      d.texts = (d.texts || []).map((t: any) => ({ ...t, value: css(t.value) }))
      d.attributes = (d.attributes || []).map((a: any) => ({ ...a, attributes: attributes(a.attributes) }))
    }
    if (d.source === 8) {
      d.adds = (d.adds || []).map((a: any) => ({ ...a, rule: css(a.rule) }))
    }
    if (d.source === 15 && d.styles) d.styles = d.styles.map((s: any) => ({ ...s, rules: (s.rules || []).map((r: any) => ({ ...r, rule: css(r.rule) })) }))
    if (d.source === 13 && d.set) d.set.value = css(d.set.value)
  }
  if (event.type === 6 || event.type === 5 && event.data?.tag !== 'ae-support-end') return null
  return event
}

export function parseReport(text: string): SupportReport {
  if (typeof text !== 'string' || text.length > MAX_REPORT_BYTES || new TextEncoder().encode(text).byteLength > MAX_REPORT_BYTES) invalid('Report exceeds the size limit')
  let r: SupportReport
  try { r = JSON.parse(text) } catch { invalid('Report is not valid JSON') }
  const pending = [{ value: r as any, depth: 0 }]
  let nodes = 0
  while (pending.length) {
    const { value, depth } = pending.pop()
    if (++nodes > 1000000 || depth > 160) invalid('Report structure exceeds the complexity limit')
    if (value && typeof value === 'object') for (const key of Object.keys(value)) {
      if (key === '__proto__' || key === 'prototype' || key === 'constructor') invalid('Unsafe report property')
      pending.push({ value: value[key], depth: depth + 1 })
    }
  }
  if (!r || r.format !== 'aeppic.support' || r.version !== 1 || r.recorder?.name !== 'rrweb' || r.recorder?.version !== '2.1.4') invalid('Unsupported report or recorder version')
  if (typeof r.id !== 'string' || typeof r.title !== 'string' || typeof r.description !== 'string' || typeof r.incomplete !== 'boolean') invalid('Invalid report metadata')
  if (!finite(r.startedAt) || !finite(r.durationMs) || r.durationMs < 0 || r.durationMs > 3600000) invalid('Invalid recording duration')
  if (!['excluded', 'embedded'].includes(r.privacy?.assets) || typeof r.privacy.sharedSensitiveRegions !== 'boolean') invalid('Invalid privacy metadata')
  if (!Array.isArray(r.events) || r.events.length < 2 || r.events.length > 200000 || r.events[0].type !== 4 || r.events[1].type !== 2) invalid('Report requires viewport metadata and a full snapshot')
  if (r.events[0].timestamp !== r.startedAt) invalid('Report start does not match its events')
  const viewport = r.events[0].data
  if (!viewport || !finite(viewport.width) || !finite(viewport.height) || viewport.width <= 0 || viewport.height <= 0 || viewport.width > 100000 || viewport.height > 100000) invalid('Invalid viewport')
  let previous = r.startedAt
  r.events = r.events.map(event => {
    if (!event || !Number.isInteger(event.type) || event.type < 0 || event.type > 5 || !finite(event.timestamp) || event.timestamp < previous || event.timestamp > r.startedAt + r.durationMs || !event.data || typeof event.data !== 'object') invalid('Invalid replay event')
    previous = event.timestamp
    let safe: SupportEvent
    try { safe = sanitizeEvent(event) } catch { invalid('Invalid replay payload') }
    if (!safe) invalid('Unsupported replay event')
    if (safe.type === 2 && (safe.data.node?.type !== 0 || !Array.isArray(safe.data.node.childNodes))) invalid('Invalid full snapshot')
    return safe
  })
  if (!Array.isArray(r.annotations) || r.annotations.length > 1000) invalid('Invalid annotations')
  for (const a of r.annotations) {
    if (!a || typeof a.id !== 'string' || typeof a.text !== 'string' || a.text.length > 10000 || !['pointer', 'rectangle', 'text'].includes(a.kind)) invalid('Invalid annotation')
    if (!finite(a.startMs) || !finite(a.endMs) || a.startMs < 0 || a.endMs < a.startMs || a.endMs > r.durationMs) invalid('Invalid annotation interval')
    for (const key of ['x', 'y', ...(a.kind === 'rectangle' ? ['width', 'height'] : [])]) if (!finite(a[key]) || a[key] < 0 || a[key] > 1) invalid('Invalid annotation coordinates')
  }
  // Reconstruct the envelope, preventing arbitrary hidden metadata from being forwarded.
  return { format: 'aeppic.support', version: 1, recorder: { name: 'rrweb', version: '2.1.4' },
    id: r.id, title: r.title, description: r.description, startedAt: r.startedAt, durationMs: r.durationMs,
    incomplete: r.incomplete, privacy: { sharedSensitiveRegions: r.privacy.sharedSensitiveRegions, assets: r.privacy.assets },
    events: r.events, annotations: r.annotations.map(a => ({ id: a.id, kind: a.kind, startMs: a.startMs, endMs: a.endMs, text: a.text, x: a.x, y: a.y, ...(a.kind === 'rectangle' ? { width: a.width, height: a.height } : {}) })) }
}
export function serializeReport(report: SupportReport): string {
  let text: string
  try { text = JSON.stringify(report) } catch { invalid('Report must be JSON serializable') }
  return JSON.stringify(parseReport(text))
}
